/**
 * Created by fers on 05.02.16.
 */
/*eslint no-console: 0*/

import React from 'react';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import {FormattedMessage, defineMessages} from 'react-intl';
import {Button} from 'react-bootstrap';
import NotEnoughPermission from './NotEnoughtPermission.react';
import FormFields, { FormSubmit } from './FormFields.react';
import mutationError from '../channel-defs/mutationError';

import PureRenderMixin from 'react-addons-pure-render-mixin';
import * as BrowserPermission from "../BrowserPermission";
import LoadingButtonNew from './LoadingButtonNew.react';
import {getMessages} from '../getMessages';

import './FormFields.scss';

const messages = defineMessages({
	leave: {id: 'basicEditor.unchanged', defaultMessage: 'There are unsaved changes on the page. Do you still want to leave?'},
});

export default class BasicEditor extends React.Component {
    static propTypes = {
        buttonTitle: PropTypes.string,
		cancel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
		cancelable: PropTypes.bool,
        children: PropTypes.node,
        columns: PropTypes.any,
        data: PropTypes.object,
        defaultValue: PropTypes.object,
        disableAutoFields: PropTypes.bool,
		disabled: PropTypes.bool,
		enableDisable: PropTypes.bool,
		focus: PropTypes.string,
        isCreate: PropTypes.bool,
        metadata: PropTypes.object.isRequired,
		noSubmit: PropTypes.bool,
        organisationId: PropTypes.string,
        parent: PropTypes.object,
        redirectToList: PropTypes.bool,
		submitLoadingButtonNewConfig: PropTypes.object,
		urlMapping: PropTypes.object,
		validationErrors: PropTypes.object,
    };

    static defaultProps = {
		disabled: false,
        disableAutoFields: false,
        isCreate: false,
        redirectToList: false,
		enableDisable: false,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            canSubmit: true,
            isLoading: false
        };

		this.form = null;

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

	componentDidMount() {
		this.saveAction = (e) => {
			if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)  && e.keyCode === 83) {
				e.preventDefault();

				if (this.state.canSubmit) {
					if (e.shiftKey) {
						this.noRedirect = true;
					}

					this.form.submit();
				}
				else {
					alert("Error: Sorry, but the form cannot be sumbit now.");
				}
			}
		};

		document.addEventListener("keydown", this.saveAction, false);
		this.mounted = true;

		if (this.props.focus) {
			let focusToInput = document.querySelector(`form${this.form.props.className ? '.' + this.form.props.className : ''} [name="${this.props.focus}"]`);

			if (focusToInput) {
				focusToInput.focus();
			}
		}
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.saveAction, false);
		window.removeEventListener('popstate', this.popstateFn);
		this.mounted = false;
	}

    enableButton = () => {
		if (this.props.enableDisable) {
			this.setState({
				canSubmit: true
			});
		}
    };

    disableButton = () => {
		if (this.props.enableDisable) {
			this.setState({
				canSubmit: false
			});
		}
    };

    isCreate() : boolean {
        return this.props.isCreate;
    }

    defaultOnFailure = (transaction) => {
		mutationError(transaction, true);
    };

    defaultOnSuccess = (data, ...args) => {
        console.log("onSuccess", data, args);
        let { urlMapping, metadata } = this.props;

        let mutationName = (this.isCreate() ? 'create' : 'update') + this.props.metadata.graphQLName();

        let object = data[mutationName][this.props.metadata.singularEntity()];

        if (!object && data[mutationName]['edge']) {
            object = data[mutationName]['edge']['node'];
        }

        if (!object && data[mutationName]['updatedNode']) {
            object = data[mutationName]['updatedNode'];
        }

		if (this.noRedirect) {
			this.noRedirect = false;
		}
		else if (this.props.redirectToList) {
            let path = urlMapping.getUrl(metadata, "list", this.props.parent ? this.props.parent : object);
            this.context.router.push(path);

        }
		else {
            let path = urlMapping.getUrl(metadata, "view", object);
            this.context.router.push(path);
        }
    };

    notifyFormError = (data) => {
		const fieldsWithErrors = this.getFieldsWithErrors();
		console.error("Invalid field", fieldsWithErrors, data);
    };

	getFieldsWithErrors() {
		return this.form.inputs.filter((input) => !input.isValid()).map(({props}) => props.name);
	}

    _basicSubmitForm = (data) => {
        this.setState({
            isLoading: true
        });

        if (this.props.isCreate !== true) {
            let primary = this.props.metadata.getPrimaryKey();

            /* in edit entity there is now primary key */
            if (primary) {
                let primaryKey = this.props.metadata.getPrimaryKey().key;

                data[primaryKey] = this.props.data[primaryKey];
            }
        }

		let stopLoading = () => {
			// this submit action can unmount the editor
			if (this.mounted) {
				this.setState({
					isLoading: false
				});
			}
		};

        return this.submitForm(data).then(() => {
			window.removeEventListener('popstate', this.popstateFn);
			window.removeEventListener('beforeunload', this.beforeunload);

			stopLoading();
        }).catch((e) => {
            stopLoading();

            throw e;
        });
    };

	cancel() {
		if (this.props.cancel) {
			this.props.cancel();
		}
		else {
			this.context.router.goBack();
		}
	}

	onFormChange() {
		if (this.formChangeBind) {
			return;
		}

		let messagesObj = getMessages();

		this.formChangeBind = true;
		this.beforeunload = (event) => {
			event.preventDefault();
			event.returnValue = ' ';
		};

		window.addEventListener('beforeunload', this.beforeunload);

		this.popstateFn = () => {
			let stayOnPage = window.confirm(messagesObj[messages.leave.id] || messages.leave.defaultMessage);

			if (stayOnPage) {
				window.removeEventListener('popstate', this.popstateFn);
				window.removeEventListener('beforeunload', this.beforeunload);
				history.back();
			} else {
				history.pushState(null, null, null);
			}
		};

		if (window.history && window.history.pushState) {
			if (!(window.history.state && 'change' in window.history.state && window.history.state.change === true)) {
				window.history.pushState({change: true}, null, null);
			}
			window.addEventListener('popstate', this.popstateFn);
		}
	}

    render() {
        if (this.props.isCreate) {
            if (!this.props.metadata.canUserCreate() || !this.props.metadata.canCreate(BrowserPermission.getPermissionObject(), null, this.props.organisationId)) {
                return <NotEnoughPermission/>;
            }
        } else if (!this.props.metadata.canUserUpdate(this.props.data) || !this.props.metadata.canUpdate(BrowserPermission.getPermissionObject(), this.props.data, this.props.organisationId)) {
            return <NotEnoughPermission/>;
        }

		let fieldsetAttr = {};

		if (this.props.disabled) {
			fieldsetAttr.disabled = "disabled";
		}

        return (
            <Formsy.Form
				className="BasicEditor"
                onBlur={this.changeValue}
				onChange={() => { this.onFormChange(); }}
                onInvalid={this.disableButton}
                onInvalidSubmit={this.notifyFormError}
                onValid={this.enableButton}
				onValidSubmit={this._basicSubmitForm}
				ref={(el) => { this.form = el; }}
				validationErrors={this.props.validationErrors}
            >
				<fieldset {...fieldsetAttr}>
					{!this.props.disableAutoFields ? <FormFields
						columns={this.props.columns}
						data={this.props.data}
						defaultValue={this.props.defaultValue}
						isCreate={this.props.isCreate ? this.props.isCreate : false}
						metadata={this.props.metadata}
						parent={this.props.parent}
					/> : null}

					{this.props.children}

					<div className="BasicEditorToolbar">
						{	this.props.noSubmit ? null :
								(this.props.submitLoadingButtonNewConfig
									? (<LoadingButtonNew
										{...this.props.submitLoadingButtonNewConfig}
										className="btn btn-primary submit"
										disabled={!this.state.canSubmit}
										isLoading={this.state.isLoading}
										type='submit'
									/>)
									: (<FormSubmit
										buttonTitle={this.props.buttonTitle}
										canSubmit={this.state.canSubmit}
										className="submit"
										isCreate={this.props.isCreate ? this.props.isCreate : false}
										isLoading={this.state.isLoading}
										metadata={this.props.metadata}
									/>)
								)
						}

						{
							this.props.cancelable || this.props.cancel
								? <Button bsStyle="link" className="btn-storno" onClick={() => { this.cancel(); }}>
									<FormattedMessage defaultMessage="Cancel" id="common.storno" />
								</Button>
								: null
						}
					</div>
				</fieldset>
            </Formsy.Form>
        );
    }
}
